import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, RotateCcw, Utensils } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api, recipeImage } from "@/lib/api";

const daysLeft = (deletedAt) => {
  if (!deletedAt) return 30;
  const gone = new Date(deletedAt).getTime();
  const left = 30 - Math.floor((Date.now() - gone) / 86400000);
  return Math.max(0, left);
};

export default function Trash() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = () => api.get("/recipes/trash").then((r) => { setItems(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  useEffect(() => { load(); }, []);

  const restore = async (r) => {
    try {
      await api.post(`/recipes/${r.id}/restore`);
      toast.success("Recette restaurée");
      load();
    } catch {
      toast.error("Impossible de restaurer");
    }
  };

  const purge = async (r) => {
    try {
      await api.delete(`/recipes/${r.id}/permanent`);
      toast.success("Supprimée définitivement");
      load();
    } catch {
      toast.error("Impossible de supprimer");
    }
  };

  return (
    <div className="px-5 pt-8" data-testid="trash-page">
      <header className="flex items-center gap-3 at-fade-up">
        <button data-testid="trash-back-btn" onClick={() => navigate("/more")} className="flex h-10 w-10 items-center justify-center rounded-full at-press" style={{ background: "var(--at-elevated)" }}>
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="font-display text-2xl font-bold">Corbeille</h1>
          <p className="text-sm" style={{ color: "var(--at-text-2)" }}>Recettes supprimées, récupérables 30 jours.</p>
        </div>
      </header>

      {loaded && items.length === 0 ? (
        <div className="mt-16 flex flex-col items-center text-center" style={{ color: "var(--at-muted)" }} data-testid="trash-empty">
          <Trash2 size={44} className="mb-3" />
          <p className="font-medium">La corbeille est vide</p>
        </div>
      ) : (
        <div className="mt-5 space-y-3">
          {items.map((r) => {
            const img = recipeImage(r);
            const left = daysLeft(r.deleted_at);
            return (
              <div key={r.id} data-testid={`trash-item-${r.id}`} className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white p-2.5 at-shadow">
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl" style={{ background: "var(--at-elevated)" }}>
                  {img ? <img src={img} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Utensils size={20} style={{ color: "var(--at-muted)" }} /></div>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{r.title}</p>
                  <p className="text-xs" style={{ color: left <= 5 ? "var(--at-brand)" : "var(--at-muted)" }}>
                    {left > 0 ? `Supprimée définitivement dans ${left} j` : "Sera supprimée aujourd'hui"}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col gap-1.5">
                  <Button data-testid={`restore-btn-${r.id}`} variant="outline" onClick={() => restore(r)}
                    className="h-9 rounded-2xl" style={{ borderColor: "var(--at-sage)", color: "var(--at-sage)" }}>
                    <RotateCcw size={15} className="mr-1.5" /> Restaurer
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button data-testid={`purge-btn-${r.id}`} variant="outline" className="h-9 rounded-2xl text-red-500 border-red-200">
                        <Trash2 size={15} className="mr-1.5" /> Supprimer
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-3xl">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer définitivement ?</AlertDialogTitle>
                        <AlertDialogDescription>« {r.title} » sera effacée immédiatement et ne pourra plus être récupérée.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Annuler</AlertDialogCancel>
                        <AlertDialogAction data-testid={`confirm-purge-${r.id}`} onClick={() => purge(r)} className="rounded-xl bg-red-500 hover:bg-red-600">Supprimer</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
